import type { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
} from '@vnetwork/ui/primitives';
import type { Asset } from '@/api/fastchannel';
import { formatBytes } from '@/pages/services/_shared/analytics-format';
import { formatMs, formatDurationShort, assetStageLabel } from './shared';

interface AssetDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: Asset;
}

/** Read-only details for a library asset, including the per-stage normalization
 *  metadata (how long each step took, input/output codecs, sizes) so an operator
 *  can see exactly what the pipeline did instead of an opaque "processing". */
export function AssetDetailsDialog({ open, onOpenChange, asset }: AssetDetailsDialogProps) {
  const m = asset.normalize_meta;
  const res = m?.source?.width && m?.source?.height ? `${m.source.width}×${m.source.height}` : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{asset.name || asset.object_key}</DialogTitle>
          <DialogDescription>Normalization details</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Section title="Asset">
              <Row label="Status" value={asset.status} />
              {asset.status === 'normalizing' && (
                <Row
                  label="Stage"
                  value={
                    asset.stage === 'encoding' && asset.progress
                      ? `${assetStageLabel(asset.stage)} ${asset.progress}%`
                      : assetStageLabel(asset.stage)
                  }
                />
              )}
              <Row
                label="Duration"
                value={asset.duration_seconds > 0 ? formatDurationShort(asset.duration_seconds) : '—'}
              />
              {asset.error && <Row label="Error" value={asset.error} />}
            </Section>

            <Section title="Source">
              <Row label="Video codec" value={m?.source?.video_codec || asset.video_codec || '—'} />
              <Row
                label="Audio codec"
                value={
                  m?.source?.audio_codec || asset.audio_codec
                    ? `${m?.source?.audio_codec || asset.audio_codec}${
                        m?.source?.audio_channels ? ` · ${m.source.audio_channels}ch` : ''
                      }`
                    : '—'
                }
              />
              <Row label="Resolution" value={res || '—'} />
            </Section>

            <Section title="Pipeline">
              <Row label="Download" value={metaPair(formatMs(m?.download?.ms), m?.download?.bytes ? formatBytes(m.download.bytes) : undefined)} />
              <Row
                label="Encode"
                value={metaPair(
                  formatMs(m?.encode?.ms),
                  m?.encode ? `${m.encode.video_codec}/${m.encode.audio_codec} · ${m.encode.segments} × ${m.encode.segment_seconds}s` : undefined,
                )}
              />
              <Row label="Upload" value={metaPair(formatMs(m?.upload?.ms), m?.upload ? `${m.upload.files} files · ${formatBytes(m.upload.bytes)}` : undefined)} />
            </Section>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Joins a primary value (e.g. duration) with an optional detail in parentheses. */
function metaPair(primary: string, detail: string | undefined): string {
  if (primary === '—' && !detail) return '—';
  return detail ? `${primary} · ${detail}` : primary;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--vn-font-size-xs)',
          fontWeight: 'var(--vn-font-weight-medium)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--vn-fg-muted)',
          marginBottom: 'var(--vn-space-2)',
        }}
      >
        {title}
      </div>
      <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-4)', fontSize: 'var(--vn-font-size-sm)' }}>
      <span style={{ color: 'var(--vn-fg-muted)' }}>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right', minWidth: 0, wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}
